import React, { useEffect, useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Plus, Search, Download, Lock, Eye, Trash2, Clock, Target, Users, BookOpen, 
  AlertTriangle, ArrowRight, X, Check, Copy, ExternalLink, RefreshCw, Command 
} from 'lucide-react'
import { toast, Toaster } from 'sonner'
import { format } from 'date-fns'

import type { 
  Aporia, FullAporia, PanelWithExports, Researcher, WorkflowJob, Event, 
  Boundary, ValueBehavior, RegretTelescope, LearningEdge 
} from './lib/types'
import * as api from './lib/api'

// ---------- Prominent Creator Links (required) ----------
function CreatorLinks({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${compact ? 'text-[11px]' : 'text-sm'} text-ink-light flex items-center gap-3 flex-wrap`}>
      <span className="text-ink-muted">a private instrument by</span>
      <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noopener noreferrer"
         className="hover:text-accent underline underline-offset-2 font-medium">Abhay</a>
      <span className="text-ink-muted">·</span>
      <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noopener noreferrer"
         className="hover:text-accent underline underline-offset-2 font-medium">buy me a coffee</a>
    </div>
  )
}

// ---------- Main App ----------
export default function Aporia() {
  const [phase, setPhase] = useState<'landing' | 'unlock' | 'app'>('landing')
  const [researcher, setResearcher] = useState<Researcher | null>(null)
  const [sessionToken, setSessionToken] = useState<string>('')

  const [view, setView] = useState<'dashboard' | 'editor' | 'panels' | 'hold' | 'settings'>('dashboard')
  const [aporias, setAporias] = useState<Aporia[]>([])
  const [selectedAporia, setSelectedAporia] = useState<FullAporia | null>(null)
  const [panels, setPanels] = useState<PanelWithExports[]>([])
  const [jobs, setJobs] = useState<WorkflowJob[]>([])
  const [events, setEvents] = useState<Event[]>([])

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'open' | 'high-embarrass'>('all')

  // Unlock form
  const [passInput, setPassInput] = useState('')
  const [handleInput, setHandleInput] = useState('')

  // Editor local state (optimistic + sync)
  const [editorDraft, setEditorDraft] = useState<any>(null)

  // Panel composer
  const [showComposer, setShowComposer] = useState(false)
  const [composer, setComposer] = useState<any>({ title: '', core: '', selected: [] as string[], includeEmb: 0, includeVB: 1, includeRegret: 1, includeEdges: 1, note: '' })

  // Hold / zen view
  const [holdAporia, setHoldAporia] = useState<FullAporia | null>(null)

  // Command palette
  const [cmdOpen, setCmdOpen] = useState(false)

  // Load persisted session on mount
  useEffect(() => {
    const saved = localStorage.getItem('aporia_session')
    if (saved) {
      try {
        const { token, rid, handle } = JSON.parse(saved)
        setSessionToken(token)
        setResearcher({ id: rid, handle })
        setPhase('app')
        bootstrapApp(token, rid)
      } catch {}
    }
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setCmdOpen(v => !v)
      }
      if (e.key === 'Escape') setCmdOpen(false)
      if (phase === 'app' && e.key.toLowerCase() === 'n' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        createNewAporia()
      }
      if (e.key.toLowerCase() === '?' && phase === 'app') {
        e.preventDefault()
        setView('settings')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase])

  async function bootstrapApp(token: string, rid: string) {
    setLoading(true)
    try {
      const [me, aps, pns, js] = await Promise.all([
        api.getMe(token, rid),
        api.listAporias(token, rid),
        api.listPanels(token, rid),
        api.listJobs(token, rid),
      ])
      setResearcher(me)
      setAporias(aps)
      setPanels(pns)
      setJobs(js)
      // seed demo data on truly fresh workspace
      if (aps.length === 0) {
        await seedDemo(token, rid)
        const fresh = await api.listAporias(token, rid)
        setAporias(fresh)
      }
    } catch (e: any) {
      toast.error('Session expired. Please unlock again.')
      localStorage.removeItem('aporia_session')
      setPhase('unlock')
    } finally { setLoading(false) }
  }

  async function seedDemo(token: string, rid: string) {
    // Create two rich realistic researcher aporias
    const a1 = await api.createAporia(token, rid, {
      title: 'Missing baseline in longitudinal qualitative interviews on AI-mediated care',
      phenomenon: 'In three separate sites, when care workers describe how they decide whether to trust an AI suggestion about patient risk, the interview transcripts contain almost no discussion of what “normal” looked like before the system was introduced. We have rich data on post-deployment sensemaking but almost nothing on the pre-existing discernment practices that the AI is supposedly augmenting or replacing.'
    })
    await api.addBoundary(token, rid, a1.id, { kind: 'known_unknown', content: 'We know the post-deployment narratives are heavily shaped by the presence of the AI dashboard and by audit cultures.' })
    await api.addBoundary(token, rid, a1.id, { kind: 'unknown_unknown', content: 'We have almost no access to what “good enough judgment” looked like in these teams in 2018–2019.' })
    await api.addEmbarrassment(token, rid, a1.id, 7, 'I worry this is a fatal flaw in the whole study design and I should have caught it in the grant.')
    await api.addCat(token, rid, a1.id, { category: 'data_absence', researcher_label: 'temporal_baseline_gap', certainty_note: 'Very high confidence this is structural, not just recruitment.' })
    await api.addValueBehavior(token, rid, a1.id, { value: 'epistemic humility', current_behavior: 'I have been writing as if the AI is the sole intervention when the real story is probably the interaction with pre-existing informal triage cultures.', alignment: -1, reflection: 'This misrepresents the phenomenon and may lead to policy recommendations that blame workers for “not using the tool properly”.' })
    await api.addRegret(token, rid, a1.id, { horizon: '2y', regret_text: 'I will regret not having at least attempted to reconstruct, through secondary sources or retired staff, what the pre-AI judgment environment actually felt like.', wish_text: 'Future me would be grateful for even three paragraphs of speculative reconstruction plus a clear statement of the absence.' })
    await api.addEdge(token, rid, a1.id, { edge_text: 'Find 2–3 retired district nurses willing to talk about 2017–2019 risk assessment culture', kind: 'conversation', parked: 0 })

    const a2 = await api.createAporia(token, rid, {
      title: 'The “absence of a language” for model drift in small clinical teams',
      phenomenon: 'Teams notice that the model “feels off” months before any metric crosses a threshold. They have sophisticated tacit ways of detecting this, but there is no sanctioned vocabulary or procedure to escalate “the model is becoming untrustworthy in ways we cannot yet quantify” without sounding like Luddites.'
    })
    await api.addBoundary(token, rid, a2.id, { kind: 'known', content: 'Statistical process control charts exist but are not used by these teams and do not capture the relational cues they actually rely on.' })
    await api.addBoundary(token, rid, a2.id, { kind: 'known_unknown', content: 'We do not know what would happen to patient outcomes or team morale if teams were given legitimate language and air cover to act on early “felt drift”.' })
    await api.addEmbarrassment(token, rid, a2.id, 4)
    await api.addCat(token, rid, a2.id, { category: 'conceptual_incoherence', researcher_label: 'tacit_vs_formal_drift_detection' })
    await api.addValueBehavior(token, rid, a2.id, { value: 'care for participants', current_behavior: 'By only studying “successful” model use I am quietly participating in the silencing of the very signals that might protect patients.', alignment: -2 })
    await api.addRegret(token, rid, a2.id, { horizon: '10y', regret_text: 'I will deeply regret having helped produce a literature that only ever talks about model performance and never about the moral injury of being unable to name when a system is quietly becoming dangerous.', wish_text: 'I wish I had written a short, citable “vocabulary of felt drift” developed with the teams themselves.' })
    await api.addEdge(token, rid, a2.id, { edge_text: 'Co-author a short “lexicon of early warnings” paper with two participating teams', kind: 'tiny_experiment', parked: 1 })

    // record events implicitly via the calls
    toast.success('Loaded two realistic example aporias to explore the instrument.')
  }

  async function performUnlock() {
    if (!passInput.trim() || !handleInput.trim()) {
      toast.error('Both a strong passphrase and a short handle are required.')
      return
    }
    setLoading(true)
    try {
      const { researcher: r, token } = await api.unlock(passInput.trim(), handleInput.trim())
      const sess = { token, rid: r.id, handle: r.handle }
      localStorage.setItem('aporia_session', JSON.stringify(sess))
      setSessionToken(token)
      setResearcher(r)
      setPhase('app')
      setPassInput('')
      await bootstrapApp(token, r.id)
      toast.success(`Workspace unlocked. Welcome back, ${r.handle}.`)
    } catch (e: any) {
      toast.error(e.message || 'Unlock failed. Check your passphrase.')
    } finally { setLoading(false) }
  }

  function lockWorkspace() {
    localStorage.removeItem('aporia_session')
    setPhase('unlock')
    setResearcher(null)
    setSessionToken('')
    setAporias([])
    setPanels([])
    setSelectedAporia(null)
    setView('dashboard')
    toast('Workspace locked on this device.')
  }

  async function createNewAporia() {
    if (!sessionToken || !researcher) return
    const title = prompt('Working title for this aporia (can be changed):', 'Untitled research tension') || 'Untitled research tension'
    const phenomenon = prompt('One paragraph describing the private complexity or the absence you are sitting with:', '') || 'Describe the situation, the missing public language or tool, and why it matters.'
    setLoading(true)
    try {
      const { id } = await api.createAporia(sessionToken, researcher.id, { title, phenomenon })
      const fresh = await api.listAporias(sessionToken, researcher.id)
      setAporias(fresh)
      const full = await api.getFullAporia(sessionToken, researcher.id, id)
      setSelectedAporia(full)
      setEditorDraft(full)
      setView('editor')
      toast.success('New aporia created. Begin with the boundaries or the regret telescope.')
    } catch (e) { toast.error('Failed to create') } finally { setLoading(false) }
  }

  async function openAporia(id: string) {
    if (!sessionToken || !researcher) return
    setLoading(true)
    try {
      const full = await api.getFullAporia(sessionToken, researcher.id, id)
      setSelectedAporia(full)
      setEditorDraft(full)
      setView('editor')
    } catch (e) { toast.error('Could not load') } finally { setLoading(false) }
  }

  async function savePhenomenon(patch: Partial<Aporia>) {
    if (!selectedAporia || !sessionToken || !researcher) return
    await api.updateAporia(sessionToken, researcher.id, selectedAporia.id, patch)
    const fresh = await api.getFullAporia(sessionToken, researcher.id, selectedAporia.id)
    setSelectedAporia(fresh)
    setEditorDraft(fresh)
    const list = await api.listAporias(sessionToken, researcher.id)
    setAporias(list)
  }

  // Rich interactive handlers (all call real API + optimistic refresh)
  async function addBoundary(kind: Boundary['kind'], content: string) {
    if (!selectedAporia) return
    await api.addBoundary(sessionToken, researcher!.id, selectedAporia.id, { kind, content })
    await refreshSelected()
  }
  async function setEmbarrassment(level: number, note?: string) {
    if (!selectedAporia) return
    await api.addEmbarrassment(sessionToken, researcher!.id, selectedAporia.id, level, note)
    await refreshSelected()
  }
  async function addCat(cat: string, label?: string, note?: string) {
    if (!selectedAporia) return
    await api.addCat(sessionToken, researcher!.id, selectedAporia.id, { category: cat, researcher_label: label, certainty_note: note })
    await refreshSelected()
  }
  async function addVB(value: string, behavior: string, alignment: number, reflection?: string) {
    if (!selectedAporia) return
    await api.addValueBehavior(sessionToken, researcher!.id, selectedAporia.id, { value, current_behavior: behavior, alignment, reflection })
    await refreshSelected()
  }
  async function updateVB(id: string, patch: any) {
    await api.updateValueBehavior(sessionToken, researcher!.id, id, patch)
    await refreshSelected()
  }
  async function addRegret(horizon: RegretTelescope['horizon'], regret?: string, wish?: string) {
    if (!selectedAporia) return
    await api.addRegret(sessionToken, researcher!.id, selectedAporia.id, { horizon, regret_text: regret, wish_text: wish })
    await refreshSelected()
  }
  async function addLearningEdge(text: string, kind: LearningEdge['kind']) {
    if (!selectedAporia) return
    await api.addEdge(sessionToken, researcher!.id, selectedAporia.id, { edge_text: text, kind, parked: 0 })
    await refreshSelected()
  }
  async function toggleParked(edgeId: string, parked: number) {
    await api.updateEdge(sessionToken, researcher!.id, edgeId, { parked })
    await refreshSelected()
  }

  async function refreshSelected() {
    if (!selectedAporia || !sessionToken || !researcher) return
    const full = await api.getFullAporia(sessionToken, researcher.id, selectedAporia.id)
    setSelectedAporia(full)
    setEditorDraft(full)
    const list = await api.listAporias(sessionToken, researcher.id)
    setAporias(list)
  }

  async function refreshAll() {
    if (!sessionToken || !researcher) return
    const [aps, pns, js] = await Promise.all([
      api.listAporias(sessionToken, researcher.id),
      api.listPanels(sessionToken, researcher.id),
      api.listJobs(sessionToken, researcher.id),
    ])
    setAporias(aps)
    setPanels(pns)
    setJobs(js)
  }

  // Panel composer actions
  async function createPanelFromComposer() {
    if (!sessionToken || !researcher) return
    if (!composer.title.trim() || !composer.core.trim() || composer.selected.length === 0) {
      toast.error('Title, core statement, and at least one aporia are required.')
      return
    }
    setLoading(true)
    try {
      const { id } = await api.createPanel(sessionToken, researcher.id, {
        title: composer.title.trim(),
        core_statement: composer.core.trim(),
        included_aporias: composer.selected.map((aid: string) => ({ id: aid, note: '' })),
        surfaced_embarrassment: composer.includeEmb,
        value_alignments_included: composer.includeVB,
        regret_excerpts_included: composer.includeRegret,
        learning_edges_included: composer.includeEdges,
        panel_note: composer.note.trim() || undefined,
      })
      const freshPanels = await api.listPanels(sessionToken, researcher.id)
      setPanels(freshPanels)
      setShowComposer(false)
      setComposer({ title: '', core: '', selected: [], includeEmb: 0, includeVB: 1, includeRegret: 1, includeEdges: 1, note: '' })
      setView('panels')
      toast.success('Panel created. Now prepare a deliberate export.')
      // auto open export options
      const newP = freshPanels.find(p => p.id === id)
      if (newP) await prepareExport(newP)
    } catch (e: any) { toast.error(e.message) } finally { setLoading(false) }
  }

  async function prepareExport(p: PanelWithExports) {
    const methods = ['self_html', 'r2_bundle', 'one_time_link', 'prepared_email'] as const
    const method = (prompt('Export method:\n1) self_html (instant beautiful standalone file)\n2) r2_bundle (private durable R2 via Workflow)\n3) one_time_link (KV token, 14d)\n4) prepared_email (copy beautiful message)\n\nEnter 1-4:', '1') || '1') as any
    const m = methods[parseInt(method) - 1] || 'self_html'

    if (m === 'self_html') {
      // Client-side rich self-contained export — fully functional offline
      const html = generateSelfContainedPanelHTML(p, selectedAporia)
      const blob = new Blob([html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${p.title.toLowerCase().replace(/\s+/g, '-')}-panel.html`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      await api.exportPanel(sessionToken, researcher!.id, p.id, 'self_html')
      await refreshAll()
      toast.success('Self-contained HTML downloaded. Open it anywhere.')
      return
    }

    setLoading(true)
    try {
      const res: any = await api.exportPanel(sessionToken, researcher!.id, p.id, m, { horizon: '14d' })
      await refreshAll()
      if (m === 'r2_bundle' && res.url) {
        window.open(res.url, '_blank')
        toast.success('R2 bundle prepared via Workflow. Link opened.')
      } else if (m === 'one_time_link' && res.token) {
        const link = `${location.origin}/p/${res.token}`
        await navigator.clipboard.writeText(link)
        toast.success('One-time link copied. Valid for 14 days or until revoked.')
      } else if (m === 'prepared_email') {
        const body = `Subject: Private metacognition panel — ${p.title}\n\n${p.core_statement}\n\nSecure access: ${res.url || 'see attached or request link from me'}\n\nThis panel captures a structured absence of public tools for discernment. It is shared with intention and consent.`
        await navigator.clipboard.writeText(body)
        toast.success('Email draft copied to clipboard. Send to yourself or a trusted colleague.')
      }
    } catch (e) { toast.error('Export failed') } finally { setLoading(false) }
  }

  async function revoke(exId: string) {
    if (!sessionToken || !researcher) return
    await api.revokeExport(sessionToken, researcher.id, exId)
    await refreshAll()
    toast('Export access revoked.')
  }

  // Full researcher erasure (uses Workflow)
  async function requestErasure() {
    if (!sessionToken || !researcher) return
    const phrase = `I accept that in 5 years I may regret deleting every record of my thinking about these absences.`
    const confirmation = prompt(`Type exactly:\n${phrase}`, '')
    if (confirmation !== phrase) { toast.error('Confirmation phrase did not match.'); return }
    setLoading(true)
    try {
      const job = await api.triggerErasure(sessionToken, researcher.id, confirmation)
      toast.success('Erasure workflow started. All data + R2 objects will be removed.')
      // In real life the client would poll /jobs; here we just lock after a beat
      setTimeout(() => {
        localStorage.removeItem('aporia_session')
        setPhase('landing')
        setResearcher(null)
        setSessionToken('')
      }, 1400)
    } catch (e) { toast.error('Could not start erasure') } finally { setLoading(false) }
  }

  // Filtered aporias
  const visibleAporias = useMemo(() => {
    let list = [...aporias]
    if (search) {
      const s = search.toLowerCase()
      list = list.filter(a => a.title.toLowerCase().includes(s) || a.phenomenon.toLowerCase().includes(s))
    }
    if (filter === 'open') list = list.filter(a => a.status === 'open' || a.status === 'held')
    if (filter === 'high-embarrass') {
      // we would need to join but for demo we keep simple
    }
    return list.sort((a, b) => b.last_touched_at - a.last_touched_at)
  }, [aporias, search, filter])

  // ---------- RENDER ----------

  if (phase === 'landing') {
    return (
      <div className="min-h-screen bg-[#f8f5f0] text-[#1c2526]">
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-7xl font-semibold tracking-[-3.2px] serif text-[#1c2526]">aporia</div>
              <div className="mt-2 text-2xl tracking-tight text-[#5c6b6d]">private instruments for researchers</div>
            </div>
            <CreatorLinks />
          </div>

          <div className="mt-12 max-w-2xl text-[15.2px] leading-relaxed text-[#3a4648] space-y-5">
            <p>Researchers routinely inhabit rich, private complexity around areas where <span className="font-medium">public tools for discernment are absent</span> — missing languages, unnameable model behaviors, temporal gaps, ethical blindspots that have no sanctioned vocabulary.</p>
            <p>This instrument helps you translate that private complexity into <span className="font-medium">shareable clarity</span> without forcing premature resolution or consensus. It respects ambiguity as a first-class research object.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="card p-5">
              <div className="font-medium text-[#2a4a3c]">Value–Behavior Alignment</div>
              <div className="mt-2 text-[#5c6b6d]">Surface which of your own values are in tension with current practice in this specific pocket of not-knowing. Decide what is worth structuring.</div>
            </div>
            <div className="card p-5">
              <div className="font-medium text-[#2a4a3c]">Future Regret Telescope</div>
              <div className="mt-2 text-[#5c6b6d]">Three time horizons. Write what your future self will regret not having captured, and what they would be grateful you wrote down today. This makes the work approachable.</div>
            </div>
            <div className="card p-5">
              <div className="font-medium text-[#2a4a3c]">Collective Metacognition Panel</div>
              <div className="mt-2 text-[#5c6b6d]">The only exportable artifact. A deliberately composed, consent-controlled unit that externalizes the structure of an uncertainty so others can build better public tools.</div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => setPhase('unlock')} className="button button-primary px-8 py-3 text-base flex items-center gap-2">
              Enter your private workspace <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => { setPhase('unlock'); setPassInput('demo-researcher-passphrase-2026'); setHandleInput('A. Researcher'); }} className="button px-6 py-3 text-base border">
              Try with demo passphrase
            </button>
          </div>

          <div className="mt-16 border-t border-[#d8d1c4] pt-8 text-xs text-[#8a9597] max-w-prose">
            Everything is private by default. Embarrassment levels and private notes never leave your control unless you explicitly include them in a panel you choose to export. Full self-serve deletion of your entire workspace is always available. No accounts, no social features, no discovery.
          </div>

          <div className="mt-8"><CreatorLinks /></div>
        </div>
      </div>
    )
  }

  if (phase === 'unlock') {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-5xl font-semibold tracking-[-2px] serif">aporia</div>
            <div className="text-[#5c6b6d] mt-1">Unlock your private research instrument</div>
          </div>

          <div className="card p-7">
            <div className="text-xs uppercase tracking-[1px] text-[#8a9597] mb-1">Your sovereign workspace</div>
            <input className="input w-full mt-4 px-4 py-3 text-base" placeholder="Short handle (e.g. A. Chen or epistemic-horizon)" value={handleInput} onChange={e => setHandleInput(e.target.value)} />
            <input type="password" className="input w-full mt-3 px-4 py-3 text-base font-mono" placeholder="Strong passphrase (store in password manager)" value={passInput} onChange={e => setPassInput(e.target.value)} />
            <div className="mt-2 text-[11px] text-[#8a9597]">This passphrase is never sent to the server after the initial unlock. It is your master key across devices.</div>

            <button onClick={performUnlock} disabled={loading} className="button button-primary w-full mt-6 py-3 text-base">Unlock workspace</button>

            <div className="mt-4 text-[11px] text-[#8a9597] leading-snug">
              On another device: enter the exact same handle + passphrase. Your data travels with the key.
            </div>
          </div>

          <div className="mt-6 text-center"><CreatorLinks compact /></div>
        </div>
      </div>
    )
  }

  // MAIN APP SHELL
  const current = selectedAporia

  return (
    <div className="min-h-screen bg-[#f8f5f0] text-[#1c2526]">
      {/* Top bar */}
      <div className="sticky top-0 z-50 border-b border-[#d8d1c4] bg-[#f8f5f0]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('dashboard'); setSelectedAporia(null) }} className="font-semibold tracking-[-0.6px] serif text-2xl hover:text-[#2a4a3c]">aporia</button>
            <div className="text-[10px] px-2 py-px rounded bg-[#f0ede5] text-[#5c6b6d] font-mono">{researcher?.handle}</div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => setView('dashboard')} className={`px-3 py-1.5 rounded ${view === 'dashboard' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Dashboard</button>
            <button onClick={() => setView('panels')} className={`px-3 py-1.5 rounded ${view === 'panels' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Panels</button>
            <button onClick={() => setView('settings')} className={`px-3 py-1.5 rounded ${view === 'settings' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Privacy</button>
            <button onClick={createNewAporia} className="button button-primary px-4 py-1.5 text-sm flex items-center gap-1.5 ml-2"><Plus className="w-3.5 h-3.5" /> New Aporia</button>
            <button onClick={() => setCmdOpen(true)} className="button px-3 py-1.5 text-sm flex items-center gap-1 border"><Command className="w-3.5 h-3.5" /> k</button>
            <button onClick={lockWorkspace} className="button px-3 py-1.5 text-sm flex items-center gap-1 border ml-1"><Lock className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-8 pb-24">
        {/* DASHBOARD */}
        {view === 'dashboard' && (
          <>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="uppercase text-[10px] tracking-[1.5px] text-[#8a9597]">Your private ledger</div>
                <div className="text-3xl tracking-tight">Aporias you are holding</div>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search titles or phenomena..." className="input pl-9 w-72 py-2 text-sm" />
                  <Search className="w-4 h-4 absolute left-3 top-3 text-[#8a9597]" />
                </div>
                <select value={filter} onChange={e => setFilter(e.target.value as any)} className="input py-2 text-sm px-3">
                  <option value="all">All</option>
                  <option value="open">Open or held</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleAporias.length === 0 && <div className="text-[#5c6b6d] col-span-full py-10 text-center border border-dashed rounded">No aporias yet. Create one to begin mapping a productive uncertainty.</div>}
              {visibleAporias.map(a => (
                <div key={a.id} onClick={() => openAporia(a.id)} className="card p-5 cursor-pointer hover:border-[#4a6b5c] group">
                  <div className="font-medium tracking-tight leading-tight group-hover:text-[#2a4a3c]">{a.title}</div>
                  <div className="text-sm text-[#5c6b6d] mt-3 line-clamp-3">{a.phenomenon}</div>
                  <div className="flex items-center gap-2 mt-4 text-[11px] text-[#8a9597]">
                    <span>{format(new Date(a.last_touched_at), 'MMM d, yyyy')}</span>
                    <span className="pill">{a.status}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-xs text-[#8a9597] max-w-prose">
              This is not a progress tracker. The goal is not to resolve these aporias but to make their structure legible enough that you (or others) can eventually design better public tools for the kind of discernment they require.
            </div>
          </>
        )}

        {/* EDITOR — full rich capture */}
        {view === 'editor' && current && (
          <div className="max-w-4xl">
            <button onClick={() => { setView('dashboard'); setSelectedAporia(null) }} className="text-xs text-[#5c6b6d] hover:underline mb-3">← back to ledger</button>

            <input className="text-3xl font-medium tracking-tight w-full bg-transparent border-none focus:outline-none" value={editorDraft?.title || current.title} onChange={e => setEditorDraft({ ...editorDraft, title: e.target.value })} onBlur={() => savePhenomenon({ title: editorDraft.title })} />

            <div className="mt-1 text-xs text-[#8a9597] flex gap-3">
              <span>Last touched {format(new Date(current.last_touched_at), 'MMM d HH:mm')}</span>
              <button onClick={() => { setHoldAporia(current); setView('hold') }} className="underline">Enter Hold (zen view)</button>
            </div>

            <div className="mt-6">
              <div className="field-label mb-1.5">The phenomenon / private complexity</div>
              <textarea className="textarea w-full h-32 p-4 text-[15px]" value={editorDraft?.phenomenon || current.phenomenon} onChange={e => setEditorDraft({ ...editorDraft, phenomenon: e.target.value })} onBlur={() => savePhenomenon({ phenomenon: editorDraft.phenomenon })} />
            </div>

            {/* KNOWLEDGE BOUNDARIES */}
            <div className="mt-10">
              <div className="uppercase text-xs tracking-widest text-[#8a9597] mb-3">Knowledge boundaries — what is known, what is not, and what cannot yet be named</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(['known', 'known_unknown', 'unknown_unknown', 'edge'] as const).map(kind => (
                  <div key={kind} className="card p-4">
                    <div className="text-xs font-medium mb-2 text-[#2a4a3c]">{kind.replace('_', ' ')}</div>
                    {(current.boundaries || []).filter(b => b.kind === kind).map(b => (
                      <div key={b.id} className="text-sm mb-3 pl-2 border-l border-[#d8d1c4]">{b.content}</div>
                    ))}
                    <AddLine onAdd={(txt) => addBoundary(kind, txt)} placeholder={`Add ${kind.replace('_', ' ')} note`} />
                  </div>
                ))}
              </div>
            </div>

            {/* EMBARRASSMENT — ethical, opt-in only */}
            <div className="mt-9 card p-5">
              <div className="flex items-center gap-2 text-sm font-medium"><AlertTriangle className="w-4 h-4" /> Embarrassment / vulnerability level (private by default)</div>
              <div className="mt-3 flex items-center gap-3">
                {[0,1,2,3,4,5,6,7,8,9,10].map(l => (
                  <button key={l} onClick={() => setEmbarrassment(l)} className={`w-7 h-7 rounded-full text-xs border ${current.embarrassment && current.embarrassment.level === l ? 'bg-[#2a4a3c] text-white border-[#2a4a3c]' : 'border-[#d8d1c4] hover:bg-white'}`}>{l}</button>
                ))}
              </div>
              <div className="text-xs text-[#8a9597] mt-1">0 = none felt. 10 = this absence feels existentially risky for the field or for my position in it. This number is never included in any export unless you explicitly choose “include embarrassment” when composing a panel.</div>
              {current.embarrassment?.private_note && <div className="text-xs mt-2 text-[#5c6b6d]">Private note on file.</div>}
              <AddLine onAdd={(txt) => setEmbarrassment(current.embarrassment?.level || 4, txt)} placeholder="Optional private note (never exported by default)" />
            </div>

            {/* UNKNOWN CATEGORIZATION */}
            <div className="mt-9">
              <div className="field-label mb-2">Unknown categorization (researcher-owned, descriptive)</div>
              <div className="flex flex-wrap gap-2">
                {(current.cats || []).map(c => <span key={c.id} className="pill">{c.researcher_label || c.category}</span>)}
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {['method_gap','conceptual_incoherence','literature_silence','data_absence','ethical_blindspot','instrument_limit','ontological_ambiguity','power_asymmetry'].map(cat => (
                  <button key={cat} onClick={() => addCat(cat)} className="text-xs px-3 py-1 border rounded hover:bg-white">{cat}</button>
                ))}
                <AddLine onAdd={(txt) => addCat('custom', txt)} placeholder="custom label" small />
              </div>
            </div>

            {/* VALUE-BEHAVIOR ALIGNMENT — core structuring decision tool */}
            <div className="mt-10">
              <div className="font-medium tracking-tight">Value–Behavior Alignment</div>
              <div className="text-sm text-[#5c6b6d] mt-1">Which of your values are in tension with what you are currently doing (or not doing) here? This is how you decide what deserves structure.</div>

              <div className="mt-4 space-y-4">
                {(current.value_behaviors || []).map((vb: ValueBehavior) => (
                  <div key={vb.id} className="card p-4">
                    <div className="font-medium text-sm">{vb.value}</div>
                    <div className="text-sm mt-1 text-[#3a4648]">{vb.current_behavior}</div>
                    <div className="mt-3">
                      <div className="flex justify-between text-[10px] text-[#8a9597]"><div>strongly misaligned</div><div>strongly aligned</div></div>
                      <div className="alignment-bar mt-1 relative" style={{ width: '100%' }}>
                        <div className="alignment-dot" style={{ left: `${((vb.alignment + 2) / 4) * 100}%` }} />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {[-2,-1,0,1,2].map(a => <button key={a} onClick={() => updateVB(vb.id, { alignment: a })} className={`text-xs px-2 py-0.5 rounded ${vb.alignment === a ? 'bg-[#2a4a3c] text-white' : 'border'}`}>{a}</button>)}
                    </div>
                    {vb.reflection && <div className="text-xs mt-2 text-[#5c6b6d] italic">“{vb.reflection}”</div>}
                  </div>
                ))}
              </div>

              <AddValueBehavior onAdd={addVB} />
            </div>

            {/* FUTURE REGRET TELESCOPE */}
            <div className="mt-10">
              <div className="font-medium tracking-tight flex items-center gap-2"><Clock className="w-4 h-4" /> Future Regret Telescope</div>
              <div className="text-sm text-[#5c6b6d]">Write to the version of yourself who will one day wish they had captured this ambiguity more clearly.</div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['6m', '2y', '10y'] as const).map(h => {
                  const r = (current.regrets || []).find(x => x.horizon === h)
                  return (
                    <div key={h} className="telescope-card card p-4 border-l-4 border-[#2a4a3c]">
                      <div className="uppercase text-[10px] tracking-widest text-[#2a4a3c] mb-2">{h === '6m' ? 'Six months' : h === '2y' ? 'Two years' : 'Ten years'}</div>
                      <div className="text-xs text-[#8a9597] mb-1">What I will regret not having written down</div>
                      <textarea className="textarea w-full h-20 text-sm" placeholder="In this horizon I will regret..." defaultValue={r?.regret_text || ''} onBlur={(e) => addRegret(h, e.target.value, r?.wish_text)} />
                      <div className="text-xs text-[#8a9597] mt-3 mb-1">What future me would be grateful I captured today</div>
                      <textarea className="textarea w-full h-20 text-sm" placeholder="Future me would be grateful if..." defaultValue={r?.wish_text || ''} onBlur={(e) => addRegret(h, r?.regret_text, e.target.value)} />
                    </div>
                  )
                })}
              </div>
            </div>

            {/* LEARNING EDGES — non-binding */}
            <div className="mt-10">
              <div className="font-medium tracking-tight">Learning edges &amp; plan (non-binding)</div>
              <div className="text-sm text-[#5c6b6d]">Tiny experiments, readings, conversations, or questions you are simply carrying. Parking something is an active choice, not a failure.</div>
              <div className="mt-3 space-y-2">
                {(current.edges || []).map((e: LearningEdge) => (
                  <div key={e.id} className="flex items-start gap-3 text-sm border-l-2 pl-3 py-0.5" style={{ borderColor: e.parked ? '#d8d1c4' : '#4a6b5c' }}>
                    <div className="flex-1">{e.edge_text} <span className="text-[#8a9597] text-xs">({e.kind})</span></div>
                    <button onClick={() => toggleParked(e.id, e.parked ? 0 : 1)} className="text-xs underline text-[#5c6b6d]">{e.parked ? 'unpark' : 'park'}</button>
                  </div>
                ))}
              </div>
              <AddEdge onAdd={addLearningEdge} />
            </div>

            <div className="mt-10 flex gap-3">
              <button onClick={() => { setHoldAporia(current); setView('hold') }} className="button px-5 py-2 text-sm flex items-center gap-2"><Eye className="w-4 h-4" /> Enter Hold (distraction-free)</button>
              <button onClick={() => { setShowComposer(true); setComposer({ ...composer, selected: [current.id] }) }} className="button button-primary px-5 py-2 text-sm flex items-center gap-2">Compose into a Metacognition Panel</button>
            </div>
          </div>
        )}

        {/* PANELS — the share/export unit */}
        {view === 'panels' && (
          <div className="max-w-3xl">
            <div className="flex justify-between items-end mb-6">
              <div>
                <div className="uppercase text-xs tracking-[1px] text-[#8a9597]">Collective Metacognition Panels</div>
                <div className="text-2xl tracking-tight">The only artifacts you deliberately make shareable</div>
              </div>
              <button onClick={() => setShowComposer(true)} className="button button-primary px-4 py-2 text-sm">Compose new panel</button>
            </div>

            {panels.length === 0 && <div className="text-[#5c6b6d]">No panels yet. Select aporias in the editor and compose a panel when you are ready to externalize the structure of an uncertainty.</div>}

            <div className="space-y-4">
              {panels.map(p => (
                <div key={p.id} className="card p-5">
                  <div className="font-medium text-lg tracking-tight">{p.title}</div>
                  <div className="text-sm mt-2 text-[#3a4648]">{p.core_statement}</div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {(p.exports || []).map((ex: any) => (
                      <div key={ex.id} className="text-xs px-2 py-1 bg-[#f0ede5] rounded flex items-center gap-2">
                        {ex.method} {ex.revoked_at ? '(revoked)' : ex.expires_at ? `· expires ${format(new Date(ex.expires_at), 'MMM d')}` : ''}
                        {!ex.revoked_at && <button onClick={() => revoke(ex.id)} className="underline text-[#8f3d3d]">revoke</button>}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button onClick={() => prepareExport(p)} className="button px-4 py-1.5 text-sm flex items-center gap-2"><Download className="w-3.5 h-3.5" /> Prepare export</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HOLD / ZEN VIEW */}
        {view === 'hold' && holdAporia && (
          <div className="max-w-3xl mx-auto zen pt-8">
            <button onClick={() => { setView('editor'); setHoldAporia(null) }} className="text-xs underline mb-8">Exit Hold</button>
            <div className="text-4xl font-medium tracking-[-1.2px] serif mb-8">{holdAporia.title}</div>
            <div className="section">
              <div className="uppercase text-xs tracking-widest text-[#2a4a3c] mb-2">The phenomenon</div>
              <div>{holdAporia.phenomenon}</div>
            </div>

            {holdAporia.regrets?.length > 0 && (
              <div className="section">
                <div className="uppercase text-xs tracking-widest text-[#2a4a3c] mb-2">What future me would regret / be grateful for</div>
                {holdAporia.regrets.map(r => (
                  <div key={r.id} className="mb-6">
                    <div className="text-xs text-[#8a9597]">{r.horizon}</div>
                    {r.regret_text && <div className="mt-1">Regret: {r.regret_text}</div>}
                    {r.wish_text && <div className="mt-1">Wish: {r.wish_text}</div>}
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-[#8a9597] mt-12 border-t pt-6">This view exists so you can simply sit with the structure of the uncertainty. No editing, no resolution demanded.</div>
          </div>
        )}

        {/* PRIVACY & SETTINGS CENTER */}
        {view === 'settings' && researcher && (
          <div className="max-w-2xl">
            <div className="text-2xl tracking-tight mb-6">Privacy &amp; sovereignty center</div>

            <div className="card p-6">
              <div className="text-sm font-medium">Your workspace key</div>
              <div className="text-xs text-[#5c6b6d] mt-1">Your handle is <span className="font-medium">{researcher.handle}</span>. The passphrase you used to unlock is the only way to access this data on other devices. Never share it.</div>
              <button onClick={lockWorkspace} className="mt-4 button px-4 py-1.5 text-sm">Lock on this device</button>
            </div>

            <div className="card p-6 mt-4">
              <div className="font-medium">Full data archive</div>
              <div className="text-sm text-[#5c6b6d] mt-1">Download a complete JSON of every aporia, boundary, alignment, regret, panel, and event. Attachments are referenced by R2 key.</div>
              <button onClick={() => api.downloadFullArchive(sessionToken, researcher.id)} className="mt-3 button px-4 py-1.5 text-sm flex items-center gap-2"><Download className="w-3.5 h-3.5" /> Download my archive</button>
            </div>

            <div className="card p-6 mt-4 border-l-4 border-[#8f3d3d]">
              <div className="font-medium text-[#8f3d3d]">Complete erasure</div>
              <div className="text-sm mt-2">This will permanently delete every aporia, panel, boundary, value alignment, regret telescope entry, learning edge, artifact in R2, and all associated records. A Workflow will handle the reliable cascade. This action is irreversible.</div>
              <button onClick={requestErasure} className="mt-4 text-sm px-4 py-1.5 border border-[#8f3d3d] text-[#8f3d3d] rounded flex items-center gap-2 hover:bg-[#8f3d3d] hover:text-white"><Trash2 className="w-3.5 h-3.5" /> Initiate full workspace erasure</button>
            </div>

            <div className="mt-8">
              <div className="uppercase text-xs tracking-widest text-[#8a9597] mb-2">Recent explicit events (your transparency log)</div>
              <div className="card p-2 text-xs font-mono max-h-64 overflow-auto">
                {events.length === 0 && <div className="p-3 text-[#5c6b6d]">Events will appear here after you perform actions. They are only ever used for your own audit and for coordinating deletion workflows.</div>}
                {events.slice(0, 12).map(ev => (
                  <div key={ev.id} className="event-row px-3 py-1.5 flex gap-3"><span>{format(new Date(ev.created_at), 'yyyy-MM-dd HH:mm')}</span><span className="text-[#2a4a3c]">{ev.type}</span></div>
                ))}
              </div>
            </div>

            <div className="mt-10 text-xs text-[#8a9597]">This instrument was built to respect ambiguity rather than resolve it. Your data is yours. The only thing that ever leaves is what you deliberately put into a panel and then choose to export under your own control.</div>
            <div className="mt-3"><CreatorLinks compact /></div>
          </div>
        )}
      </div>

      {/* Composer modal */}
      <AnimatePresence>
        {showComposer && (
          <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => setShowComposer(false)}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-white w-full max-w-2xl rounded-lg border p-7" onClick={e => e.stopPropagation()}>
              <div className="font-medium text-xl tracking-tight">Compose Collective Metacognition Panel</div>
              <div className="text-sm text-[#5c6b6d] mt-1">This is the only artifact designed to leave your private workspace. Be deliberate.</div>

              <input className="input w-full mt-5 px-4 py-2.5" placeholder="Panel title" value={composer.title} onChange={e => setComposer({ ...composer, title: e.target.value })} />
              <textarea className="textarea w-full mt-3 h-28 p-4 text-sm" placeholder="Core statement: the specific absence of public tools for discernment that this panel makes visible..." value={composer.core} onChange={e => setComposer({ ...composer, core: e.target.value })} />

              <div className="mt-4 text-xs uppercase tracking-widest text-[#8a9597]">Include from which aporias</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {aporias.map(a => {
                  const sel = composer.selected.includes(a.id)
                  return <button key={a.id} onClick={() => setComposer({ ...composer, selected: sel ? composer.selected.filter((x: string) => x !== a.id) : [...composer.selected, a.id] })} className={`text-xs px-3 py-1 rounded border ${sel ? 'bg-[#2a4a3c] text-white border-[#2a4a3c]' : ''}`}>{a.title.slice(0, 48)}</button>
                })}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!composer.includeVB} onChange={e => setComposer({ ...composer, includeVB: e.target.checked ? 1 : 0 })} /> Include value–behavior alignments</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!composer.includeRegret} onChange={e => setComposer({ ...composer, includeRegret: e.target.checked ? 1 : 0 })} /> Include regret telescope excerpts</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!composer.includeEdges} onChange={e => setComposer({ ...composer, includeEdges: e.target.checked ? 1 : 0 })} /> Include learning edges</label>
                <div className="flex items-center gap-2">
                  <span>Embarrassment:</span>
                  <select value={composer.includeEmb} onChange={e => setComposer({ ...composer, includeEmb: parseInt(e.target.value) })} className="input text-xs py-0.5">
                    <option value={0}>Strip entirely</option>
                    <option value={1}>Generalize / anonymize</option>
                    <option value={2}>Include with explicit consent note</option>
                  </select>
                </div>
              </div>

              <textarea className="textarea w-full mt-4 h-16 text-sm" placeholder="Optional note to the recipient or future reader of this panel..." value={composer.note} onChange={e => setComposer({ ...composer, note: e.target.value })} />

              <div className="mt-5 flex gap-3">
                <button onClick={createPanelFromComposer} className="button button-primary flex-1 py-2.5">Create panel &amp; prepare export</button>
                <button onClick={() => setShowComposer(false)} className="button flex-1 py-2.5">Cancel</button>
              </div>
              <div className="text-[11px] text-[#8a9597] mt-3">Once created you will be offered self-contained HTML, private R2 bundle (via Workflow), one-time link, or prepared email draft.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Command palette */}
      <AnimatePresence>
        {cmdOpen && (
          <div className="cmdk" onClick={() => setCmdOpen(false)}>
            <div className="cmdk-inner" onClick={e => e.stopPropagation()}>
              <div className="p-3 text-sm border-b">Command palette</div>
              <div className="p-2 text-sm">
                {[
                  { label: 'New aporia', action: () => { setCmdOpen(false); createNewAporia() } },
                  { label: 'Go to Panels', action: () => { setCmdOpen(false); setView('panels') } },
                  { label: 'Go to Privacy center', action: () => { setCmdOpen(false); setView('settings') } },
                  { label: 'Lock workspace', action: () => { setCmdOpen(false); lockWorkspace() } },
                ].map((c, i) => (
                  <div key={i} onClick={c.action} className="px-4 py-2 hover:bg-[#f8f5f0] cursor-pointer rounded flex justify-between items-center">{c.label}<ArrowRight className="w-3.5 h-3.5 text-[#8a9597]" /></div>
                ))}
              </div>
              <div className="p-3 text-[10px] text-[#8a9597] border-t">⌘K to toggle • ESC to close</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors closeButton />
    </div>
  )

  async function refreshSelectedAndList() {
    // helper used above
    if (!selectedAporia || !sessionToken || !researcher) return
    const full = await api.getFullAporia(sessionToken, researcher.id, selectedAporia.id)
    setSelectedAporia(full)
    const list = await api.listAporias(sessionToken, researcher.id)
    setAporias(list)
  }
}

// Small inline components
function AddLine({ onAdd, placeholder, small = false }: { onAdd: (v: string) => void; placeholder: string; small?: boolean }) {
  const [v, setV] = useState('')
  return (
    <div className="flex gap-2 mt-2">
      <input className={`input flex-1 ${small ? 'text-xs py-1' : 'text-sm py-1.5'}`} placeholder={placeholder} value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && v.trim()) { onAdd(v.trim()); setV('') } }} />
      <button onClick={() => { if (v.trim()) { onAdd(v.trim()); setV('') } }} className="button px-3 text-xs">Add</button>
    </div>
  )
}

function AddValueBehavior({ onAdd }: { onAdd: (v: string, b: string, a: number, r?: string) => void }) {
  const [val, setVal] = useState(''); const [beh, setBeh] = useState(''); const [ali, setAli] = useState(0); const [refl, setRefl] = useState('')
  return (
    <div className="mt-3 card p-3 text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input className="input text-sm px-3 py-1.5" placeholder="Value (e.g. epistemic humility)" value={val} onChange={e => setVal(e.target.value)} />
        <input className="input text-sm px-3 py-1.5" placeholder="Current behavior or its absence" value={beh} onChange={e => setBeh(e.target.value)} />
      </div>
      <div className="flex gap-2 mt-2 items-center">
        <span className="text-xs text-[#8a9597]">Alignment</span>
        {[-2,-1,0,1,2].map(a => <button key={a} onClick={() => setAli(a)} className={`px-2 py-0.5 text-xs rounded ${ali === a ? 'bg-[#2a4a3c] text-white' : 'border'}`}>{a}</button>)}
        <input className="input flex-1 text-xs px-2 py-1 ml-2" placeholder="short reflection (optional)" value={refl} onChange={e => setRefl(e.target.value)} />
        <button onClick={() => { if (val && beh) { onAdd(val, beh, ali, refl || undefined); setVal(''); setBeh(''); setAli(0); setRefl('') } }} className="button px-3 text-xs">Add alignment</button>
      </div>
    </div>
  )
}

function AddEdge({ onAdd }: { onAdd: (text: string, kind: any) => void }) {
  const [txt, setTxt] = useState(''); const [k, setK] = useState<'tiny_experiment' | 'conversation' | 'parked_question'>('conversation')
  return (
    <div className="flex gap-2 mt-3">
      <input className="input flex-1 text-sm" placeholder="Next edge or question to carry" value={txt} onChange={e => setTxt(e.target.value)} />
      <select className="input text-sm px-2" value={k} onChange={e => setK(e.target.value as any)}>
        <option value="conversation">conversation</option>
        <option value="tiny_experiment">tiny experiment</option>
        <option value="parked_question">parked question</option>
      </select>
      <button onClick={() => { if (txt) { onAdd(txt, k); setTxt('') } }} className="button px-3 text-xs">Add</button>
    </div>
  )
}

// Self-contained beautiful HTML panel generator (client side, zero deps at runtime)
function generateSelfContainedPanelHTML(panel: any, sampleAporia?: any) {
  const date = new Date().toLocaleDateString()
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${panel.title}</title>
<style>body{font-family:system-ui,-apple-system,serif;margin:40px auto;max-width:720px;line-height:1.7;color:#1c2526;background:#f8f5f0;padding:20px} .meta{font-size:12px;color:#5c6b6d} h1{font-size:28px;letter-spacing:-1px} .core{font-size:17px;border-left:4px solid #2a4a3c;padding-left:18px;margin:28px 0} section{margin:32px 0} .small{font-size:12px;color:#5c6b6d}</style>
</head><body>
<h1>${panel.title}</h1>
<div class="meta">Collective Metacognition Panel • generated ${date} • aporia instrument</div>
<div class="core">${panel.core_statement}</div>
<section><strong>Why this panel exists</strong><p>This document externalizes the structure of a private research uncertainty in the specific hope that others may one day build better public tools for the kind of discernment it requires. It deliberately preserves ambiguity rather than forcing resolution.</p></section>
${panel.panel_note ? `<section><strong>Note from the researcher</strong><p>${panel.panel_note}</p></section>` : ''}
<div class="small">This is a deliberate, consent-controlled export from a private workspace. No social platform was involved. Embarrassment data was handled according to the researcher’s explicit choices at export time.</div>
</body></html>`
}
